// ============================================================================
// LES PRIMITIVES DE GRAPHIQUE — barres, sparkline, donut, jauge.
//
// ⚠️ AUCUNE BIBLIOTHÈQUE. Quatre formes suffisent au tableau de bord, et chacune tient en quelques
// dizaines de lignes de SVG ou de flex. Embarquer un moteur de graphiques pour ça coûterait des
// centaines de kilo-octets, imposerait son propre vocabulaire de couleurs, et rendrait la fidélité à la
// maquette plus difficile, pas moins.
//
// ⚠️ AUCUN NOMBRE ARBITRAIRE : géométries et couleurs viennent des jetons (`CHART`, `--series-*`).
//
// ⚠️ CE QUE CES FORMES NE FONT PAS. Pas de second axe — deux mesures d'échelles différentes font deux
// graphiques, jamais deux échelles verticales (c'est l'erreur de graphique la plus répandue). Pas de
// couleur attribuée au RANG : une teinte suit l'entité, sinon un filtre repeint les survivants et
// oblige à relire la légende.
//
// ⚠️ LE TEXTE PORTE DES JETONS D'ENCRE, jamais la couleur de sa série : c'est la pastille à côté du
// libellé qui porte l'identité. Un nombre écrit en vert pâle sur du papier est illisible, et la couleur
// n'y ajoute rien qu'on ne lise déjà.
// ============================================================================
import { useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  BARS, CHART, CHART_TIP, DONUT, DONUT_CIRCUMFERENCE, RING_CIRCUMFERENCE, barLayout, barX, donutVar,
  gaugeDash, ringSegments, seriesVar,
  sparklinePoints,
  DONUT_MIN_SHARE_BP, GAUGE_TONES, gaugeTone, type GaugeThresholds, type GaugeTone,
} from '@orgazone/ui-tokens';
import { usePlanAuDessusDeLHote } from './overlay';
import { t } from '@orgazone/i18n';

/**
 * Une pastille de couleur suivie de son libellé.
 * ⚠️ Le TEXTE reste en encre : c'est la pastille qui porte l'identité. Un libellé écrit dans la couleur
 * de sa série devient illisible dès que la teinte est claire, et n'ajoute rien qu'on ne lise déjà.
 */
function StatusPill({ couleur, texte, appuye }: { couleur: string; texte: string; appuye?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <span aria-hidden style={{ width: 9, height: 9, borderRadius: 'var(--r-r3)', background: couleur, flexShrink: 0 }} />
      <span style={{
        fontSize: 'var(--t-body)', whiteSpace: 'nowrap',
        color: appuye ? 'var(--ink-700)' : 'var(--ink-500)',
        fontWeight: appuye ? 500 : undefined,
      }}>{texte}</span>
    </span>
  );
}

/**
 * L'INFOBULLE DU GRAPHIQUE — elle suit le curseur tant qu'on reste dans la zone.
 *
 * ⚠️ EN PORTAIL ET EN POSITION FIXE : la carte a son propre `overflow`, et une bulle posée dedans
 * serait rognée dès qu'on survole un intervalle proche d'un bord. Les coordonnées sont donc celles de
 * la FENÊTRE, pas celles du graphique.
 * ⚠️ ELLE NE SE POSE JAMAIS SOUS LE POINTEUR (décalage +14 / −8) : sous le curseur, elle serait
 * masquée par la main et par le pointeur lui-même.
 * ⚠️ ELLE EST BORNÉE À DROITE : sans cette réserve, elle sort de la fenêtre dès qu'on survole les
 * derniers intervalles — ceux de la fin de période, que l'on regarde le plus souvent.
 * ⚠️ AUCUN `role="status"` : la bulle change à chaque pixel de déplacement, et une région vivante
 * l'annoncerait sans fin. C'est le `aria-label` de chaque intervalle qui porte l'information.
 */
// ⚠️ EXPORTÉE (2026-08-24, demande utilisateur : « le même design de tooltip PARTOUT ») : les charts
// composés hors du socle — l'histogramme hebdo de la fiche article vivait sur un `title` natif du
// navigateur — réutilisent CETTE bulle au lieu d'en dessiner une autre.
export function ChartTip({ pointeur, etiquette, valeur, sous }: {
  pointeur: { x: number; y: number }; etiquette: string; valeur: string; sous?: string;
}) {
  // ⚠️ SON PLAN DÉPEND DE SON HÔTE (correctif 2026-08-25). Le portail sur `document.body` lui fait
  // perdre son contexte d'empilement : avec son plan absolu (680, sous tout ce qui s'ouvre), une bulle
  // née DANS une modale — l'onglet Stats de la fiche article — se rendait DERRIÈRE elle. Le code était
  // juste, l'écran ne montrait rien. Se caler au-dessus de son seul hôte respecte la doctrine à la
  // lettre : la bulle reste couverte par tout ce qui s'ouvre PAR-DESSUS cet hôte.
  const plan = usePlanAuDessusDeLHote(CHART_TIP.z);
  return createPortal(
    <div
      aria-hidden
      style={{
        position: 'fixed', pointerEvents: 'none', zIndex: plan,
        left: Math.min(pointeur.x + CHART_TIP.offsetX, window.innerWidth - CHART_TIP.edgeReserve),
        top: pointeur.y + CHART_TIP.offsetY,
        minWidth: CHART_TIP.minWidth,
        padding: `${CHART_TIP.padV}px ${CHART_TIP.padH}px`,
        borderRadius: `var(--r-${CHART_TIP.radius})`, boxShadow: CHART_TIP.shadow,
        background: 'var(--ink-900)', color: 'var(--paper)',
      }}
    >
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: CHART_TIP.labelSize,
        letterSpacing: CHART_TIP.labelTracking, textTransform: 'uppercase', opacity: CHART_TIP.labelOpacity,
      }}>{etiquette}</div>
      <div style={{
        fontSize: CHART_TIP.valueSize, fontWeight: 700, marginTop: 3, fontVariantNumeric: 'tabular-nums',
      }}>{valeur}</div>
      {sous ? (
        <div style={{
          fontSize: CHART_TIP.subSize, opacity: CHART_TIP.subOpacity, marginTop: 1, fontVariantNumeric: 'tabular-nums',
        }}>{sous}</div>
      ) : null}
    </div>,
    document.body,
  );
}

/** Ce que l'infobulle d'un graphique dit d'une marque survolée : son libellé, sa valeur, et un détail facultatif. */
export interface ChartTipContent {
  label: string;
  value: string;
  detail?: string;
}

/** Les attributs qu'une marque reçoit pour porter l'infobulle. */
export interface ChartTipProps {
  'aria-label'?: string;
  onMouseEnter: (e: ReactMouseEvent) => void;
  onMouseMove: (e: ReactMouseEvent) => void;
  onMouseLeave: () => void;
}

/**
 * LE GESTE DE L'INFOBULLE, pour un graphique composé hors du socle (colonnes en flex, segments, cases, lignes de barres) :
 * chaque marque déclare ce que la bulle dit d'elle, la bulle suit le pointeur et disparaît à la sortie.
 * ⚠️ LE MÊME TEXTE NOMME LA MARQUE (`aria-label`) : la bulle est muette pour un lecteur d'écran (`aria-hidden`), et c'est
 *    ce que le `title` du navigateur portait — le retirer sans le reprendre rendrait le graphique muet. Une marque qui a
 *    déjà son nom (un bouton qui dit sa saison) le garde : `{ names: false }`.
 * ⚠️ UNE BULLE PAR GRAPHIQUE, PAS PAR MARQUE : l'état vit dans le graphique, qui rend `tip` une seule fois.
 */
export function useChartTip(): { tipProps: (content: ChartTipContent, options?: { names?: boolean }) => ChartTipProps; tip: ReactNode } {
  const [hover, setHover] = useState<{ content: ChartTipContent; x: number; y: number } | null>(null);
  const tipProps = (content: ChartTipContent, { names = true }: { names?: boolean } = {}): ChartTipProps => {
    const aim = (e: ReactMouseEvent) => setHover({ content, x: e.clientX, y: e.clientY });
    return {
      ...(names ? { 'aria-label': [content.label, content.value, content.detail].filter(Boolean).join(' · ') } : {}),
      onMouseEnter: aim,
      onMouseMove: aim,
      onMouseLeave: () => setHover(null),
    };
  };
  const tip = hover
    ? <ChartTip pointeur={{ x: hover.x, y: hover.y }} etiquette={hover.content.label} valeur={hover.content.value} sous={hover.content.detail} />
    : null;
  return { tipProps, tip };
}

/**
 * LA LÉGENDE DES DEUX SÉRIES — la période affichée et son point de comparaison.
 *
 * ⚠️ ELLE NE VIT PAS DANS LE GRAPHIQUE, et c'est un choix de la maquette : elle est posée au bout de
 * la ligne des périodes, dans le bandeau. Elle nomme donc ce que TOUT l'écran compare, pas seulement
 * cette carte — et la carte gagne la hauteur qu'une légende interne lui aurait prise.
 * ⚠️ La période AFFICHÉE est appuyée (encre plus sombre, graisse 500), la référence est en retrait :
 * deux libellés de même poids laisseraient hésiter sur celui qu'on regarde.
 */
export function SeriesLegend({ current, previous }: { current: string; previous: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <StatusPill couleur="var(--teal-500)" texte={current} appuye />
      <StatusPill couleur="var(--ink-300)" texte={previous} />
    </div>
  );
}

// ── BARRES ────────────────────────────────────────────────────────────────────────────────────────

export interface BarPoint {
  /** Clé technique (heure « 14 », jour « 20260816 ») — sert d'identité, pas d'affichage. */
  key: string;
  value: number;
  /** Étiquette d'axe. Absente = la clé. */
  label?: string;
  /**
   * Valeur de COMPARAISON du même intervalle (l'an dernier, le mois précédent…).
   * ⚠️ `null` ou absente ⇒ AUCUNE barre de comparaison n'est dessinée pour cet intervalle. Ce n'est
   * pas un zéro : « pas de vis-à-vis » et « rien vendu l'an dernier » ne se lisent pas pareil, et une
   * barre à zéro affirmerait le second.
   */
  compare?: number | null;
}

export interface BarsProps {
  points: BarPoint[];
  /** Hauteur de la zone tracée, hors étiquettes d'axe. */
  height?: number;
  /** Rendu HUMAIN d'une valeur (montant, quantité) — l'infobulle et l'accessibilité s'en servent. */
  format: (v: number) => string;
  /** Une barre sur N porte son étiquette. À 1, toutes ; utile quand l'axe se densifie. */
  labelEvery?: number;
  /**
   * Les DEUX séries nommées, quand les points portent une comparaison.
   * ⚠️ OBLIGATOIRE dès qu'il y a deux séries, et le composant ne dessine la seconde que si elles sont
   * fournies. Elles nomment les séries dans l'infobulle.
   * ⚠️ LA LÉGENDE EST À LA CHARGE DE L'APPELANT (`SeriesLegend`), parce que la maquette la pose dans le
   * bandeau de l'écran et non dans la carte. Elle n'en reste pas moins DUE : deux couleurs sans
   * légende ne se lisent pas — le lecteur devine, ou renonce.
   */
  series?: { current: string; previous: string };
}

/**
 * Histogramme à UNE série — la courbe d'activité du tableau de bord.
 *
 * ⚠️ EN FLEX ET NON EN SVG, à dessein : les étiquettes d'axe de la maquette sont des cellules `flex:1`,
 * et une barre en flex se cale exactement dessus à toutes les largeurs. Un SVG imposerait de recalculer
 * les positions à chaque redimensionnement pour aligner deux systèmes de coordonnées.
 *
 * ⚠️ LE BOUT ARRONDI EST DU CÔTÉ DE LA VALEUR SEULEMENT : la base reste plate, posée sur la ligne de
 * zéro. Arrondir les quatre coins ferait flotter les barres au-dessus de leur origine.
 *
 * ⚠️ UNE BARRE À ZÉRO GARDE UN TRAIT VISIBLE. Sans lui, « aucune vente à 14 h » et « pas de créneau à
 * 14 h » se ressemblent — or ce n'est pas la même information.
 */
/**
 * LARGEUR RÉELLE d'un élément, suivie à la trace.
 * ⚠️ Un histogramme dont les barres ont une largeur EN PIXELS doit connaître son cadre : sans mesure,
 * il faudrait un `viewBox` fixe étiré par le navigateur, et la largeur des barres varierait avec la
 * taille de la fenêtre — un plafond de 22 px ne voudrait alors plus rien dire.
 * ⚠️ Le repli à 560 px est celui de la maquette : il sert au premier rendu, avant la mesure.
 */
function useLargeur(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [largeur, setLargeur] = useState(560);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const suivre = () => setLargeur(Math.max(1, Math.round(el.getBoundingClientRect().width)));
    suivre();
    // ⚠️ `ResizeObserver` et non l'événement `resize` de la fenêtre : la carte change aussi de largeur
    // quand le rail se replie ou qu'une carte voisine est masquée, sans que la fenêtre bouge.
    const ro = new ResizeObserver(suivre);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, largeur];
}

/**
 * Histogramme à UNE ou DEUX séries — la courbe d'activité du tableau de bord.
 *
 * ⚠️ EN SVG, ET AVEC DES BARRES À LARGEUR PLAFONNÉE. Une barre qui occupe tout son créneau devient un
 * pavé dès que les intervalles sont peu nombreux : six mois affichés au mois donneraient six blocs de
 * 90 px de large, où l'on ne lit plus une série mais un aplat. La paire occupe 62 % du créneau, 22 px
 * par barre au maximum, et le reste est le blanc qui permet de compter les intervalles.
 *
 * ⚠️ LA RÉFÉRENCE (l'an dernier) EST EN ENCRE NEUTRE, pas dans une seconde teinte de série. Ce ne sont
 * pas deux catégories de même rang : c'est la période qu'on REGARDE et son point de comparaison. Deux
 * couleurs vives les mettraient à égalité, et l'œil ne saurait plus laquelle est l'année en cours.
 * ⚠️ Cette encre est posée ICI et non laissée à l'appelant : un écran qui donnerait à la référence une
 * teinte de marque casserait la lecture sans que rien ne le signale.
 *
 * ⚠️ TROIS LIGNES DE REPÈRE AUX QUARTS : sans elles, comparer deux barres éloignées oblige à suivre le
 * bord du cadre, trop loin pour être utile.
 *
 * ⚠️ UNE BARRE À ZÉRO GARDE UN TRAIT VISIBLE. Sans lui, « aucune vente à 14 h » et « pas de créneau à
 * 14 h » se ressemblent — or ce n'est pas la même information.
 *
 * ⚠️ DEUX SÉRIES PARTAGENT UNE SEULE ÉCHELLE, calculée sur les deux : c'est ce qui rend la comparaison
 * honnête. Deux échelles feraient paraître égales deux valeurs qui ne le sont pas — l'erreur de
 * graphique la plus répandue, et elle est ici invisible puisque l'axe n'est pas chiffré.
 */
export function Bars({ points, height = BARS.height, format, labelEvery = 1, series }: BarsProps) {
  const [survol, setSurvol] = useState<number | null>(null);
  const [cadre, largeur] = useLargeur();
  const compare = !!series && points.some((p) => p.compare != null);
  // ⚠️ La marge haute empêche la plus grande barre de toucher le bord : sans elle, rien ne distingue
  // « au maximum » de « coupée ».
  const max = Math.max(1, ...points.flatMap((p) => [Math.abs(p.value), compare ? Math.abs(p.compare ?? 0) : 0])) * BARS.headroom;
  const { slot, pairWidth, barWidth } = barLayout(largeur, points.length);
  const h = (v: number) => Math.max(2, (Math.abs(v) / max) * height);

  // ⚠️ ELLE SUIT LE CURSEUR, comme la maquette : sur une série dense, une bulle posée à un endroit
  // fixe oblige à chercher quelle barre elle décrit. Sous le pointeur, la réponse est là où l'œil est
  // déjà. Les coordonnées sont celles de la FENÊTRE (`clientX/Y`) et la bulle est en `fixed` : elle
  // échappe ainsi au `overflow` de la carte, qui la rognerait près des bords.
  const [pointeur, setPointeur] = useState<{ x: number; y: number } | null>(null);
  const viser = (i: number, e: { clientX: number; clientY: number }) => {
    setSurvol(i);
    setPointeur({ x: e.clientX, y: e.clientY });
  };
  const lacher = () => { setSurvol(null); setPointeur(null); };

  return (
    <div>
      {/* ⚠️ `color` porte l'encre de la RÉFÉRENCE et des lignes de repère : le SVG les prend en
          `currentColor`, ce qui les fait suivre le thème sans qu'aucune couleur soit figée ici. */}
      <div ref={cadre} style={{ position: 'relative', color: 'var(--ink-300)' }}>
        <svg
          viewBox={`0 0 ${largeur} ${height}`}
          width={largeur}
          height={height}
          style={{ width: '100%', height, display: 'block', overflow: 'visible' }}
          role="list"
          aria-label={t('Histogramme de {n} intervalles', { n: points.length })}
        >
          {Array.from({ length: BARS.guides }, (_, g) => {
            const y = height - (height * (g + 1)) / (BARS.guides + 1);
            return (
              <line
                key={`g${g}`} x1={0} x2={largeur} y1={y} y2={y}
                stroke="currentColor" strokeOpacity={BARS.guideOpacity} strokeWidth={1}
              />
            );
          })}
          {points.map((p, i) => {
            const actif = survol === i;
            const x0 = barX(i, slot, pairWidth);
            const hn = h(p.value);
            const hp = p.compare == null ? null : h(p.compare);
            const dit = p.compare == null
              ? `${p.label ?? p.key} : ${format(p.value)}`
              : t('{label} : {valeur}, {precedent} en {periode}', { label: p.label ?? p.key, valeur: format(p.value), precedent: format(p.compare), periode: series?.previous ?? '' });
            return (
              // ⚠️ L'infobulle est une aide à la SOURIS : sans ce libellé, l'information n'existerait
              // pas pour un lecteur d'écran ni pour qui navigue au clavier.
              <g key={p.key} role="listitem" aria-label={dit}>
                {/* La RÉFÉRENCE d'abord, à gauche : on lit l'an dernier puis l'année en cours. */}
                {hp !== null ? (
                  <rect
                    x={x0} y={height - hp} width={barWidth} height={hp} rx={BARS.barRadius}
                    fill="currentColor" fillOpacity={actif ? BARS.refOpacityHover : BARS.refOpacity}
                  />
                ) : null}
                <rect
                  x={hp === null ? x0 : x0 + barWidth + BARS.pairGap}
                  y={height - hn}
                  width={hp === null ? pairWidth : barWidth}
                  height={hn}
                  rx={BARS.barRadius}
                  fill="var(--teal-500)"
                  fillOpacity={actif ? BARS.nowOpacityHover : BARS.nowOpacity}
                />
                {/* ⚠️ La CIBLE de survol est le créneau ENTIER : viser une barre de quelques pixels de
                    large à la souris est un exercice, pas une interaction. */}
                <rect
                  x={i * slot} y={0} width={slot} height={height} fill="transparent"
                  onMouseEnter={(e) => viser(i, e)}
                  onMouseMove={(e) => viser(i, e)}
                  onMouseLeave={lacher}
                />
              </g>
            );
          })}
        </svg>
      </div>
      {survol !== null && pointeur && points[survol] ? (
        <ChartTip
          pointeur={pointeur}
          etiquette={points[survol].label ?? points[survol].key}
          valeur={format(points[survol].value)}
          sous={points[survol].compare != null ? t('{precedent} en {periode}', { precedent: format(points[survol].compare), periode: series?.previous ?? '' }) : undefined}
        />
      ) : null}
      <div style={{ display: 'flex', marginTop: 8 }}>
        {points.map((p, i) => (
          <div
            key={p.key}
            style={{
              flex: 1, textAlign: 'center', fontSize: 'var(--t-meta)', color: 'var(--ink-500)',
              fontVariantNumeric: 'tabular-nums', overflow: 'hidden', whiteSpace: 'nowrap',
            }}
          >
            {i % labelEvery === 0 ? (p.label ?? p.key) : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SPARKLINE ─────────────────────────────────────────────────────────────────────────────────────

export interface SparklineProps {
  values: number[];
  height?: number;
  /** Teinte du tracé — défaut : l'accent de marque. */
  stroke?: string;
}

/**
 * Micro-courbe d'une tuile de KPI : une TENDANCE, pas une lecture de valeurs.
 *
 * ⚠️ NI AXE NI GRADUATION, et c'est le propre de la forme : elle répond à « ça monte ou ça descend ? ».
 * Y ajouter une échelle ferait croire qu'on peut y lire un chiffre, alors que la tuile porte déjà le
 * chiffre en 25 px juste au-dessus.
 * ⚠️ `preserveAspectRatio="none"` : la courbe s'étire à la largeur de la carte. C'est acceptable ICI
 * parce qu'aucune pente n'est à mesurer — ce serait faux sur un graphique qu'on interprète.
 */
export function Sparkline({ values, height = CHART.sparkHeight, stroke = 'var(--teal-500)' }: SparklineProps) {
  if (values.length < 2) {
    // Un point unique ne fait pas une tendance : on ne trace rien plutôt qu'un trait plat trompeur.
    return <div style={{ height }} aria-hidden="true" />;
  }
  const pts = sparklinePoints(values).map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`);

  return (
    <svg width="100%" height={height} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" style={{ display: 'block' }}>
      {/* ⚠️ UN APLAT, PAS UN DÉGRADÉ (relevé maquette) : le dégradé s'évanouissait vers le bas et
          faisait paraître la série plus faible qu'elle n'est sur une tuile à fond clair. */}
      <polygon points={`0,100 ${pts.join(' ')} 100,100`} fill={stroke} fillOpacity={CHART.areaOpacity} />
      <polyline
        points={pts.join(' ')}
        fill="none"
        stroke={stroke}
        strokeWidth={CHART.lineWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        // ⚠️ Sans cela, l'étirement horizontal écraserait aussi l'épaisseur du trait : la courbe
        // s'amincirait sur une carte large et s'épaissirait sur une carte étroite.
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

// ── ANNEAUX (donut & jauge) ───────────────────────────────────────────────────────────────────────

/** Géométrie de la maquette : `viewBox` 36, anneau de rayon 15,5 et d'épaisseur 5. */
const BOITE = 36;

export interface DonutSlice {
  key: string;
  label: string;
  value: number;
}

/**
 * REPLIE LA QUEUE D'UNE RÉPARTITION dans une part « Autres ».
 *
 * ⚠️ POURQUOI ELLE EXISTE : un anneau n'a que six teintes distinctes. Au-delà, il faudrait soit
 * CYCLER (deux parts de la même couleur — le lecteur croit lire deux fois la même chose), soit
 * PLAFONNER (toutes les parts du bout se confondent, ce qui a été livré une fois : trois vendeurs
 * violets sur quatre). Les deux mentent ; nommer le reste, non.
 * ⚠️ LE RESTE EST UNE SOMME, PAS UNE TRONCATURE : le total de l'anneau reste celui des données. Une
 * liste coupée à six ferait un anneau dont les parts n'additionnent plus à 100 %.
 * ⚠️ Rien n'est replié quand il n'y a qu'UN élément en trop : « Autres (1) » n'apprend rien de plus
 * que son nom, et coûte une teinte pour rien.
 * ⚠️ GÉNÉRIQUE pour que les champs de l'appelant (un décompte de tickets, par exemple) survivent au
 * repliement : typée sur `DonutSlice`, la fonction les aurait effacés du type de retour alors qu'ils
 * existent à l'exécution — l'écran aurait cessé d'afficher une colonne sans qu'aucune erreur ne sorte.
 */
export function foldTail<T extends DonutSlice>(
  slices: T[], max: number, label = t('Autres'),
): Array<T | DonutSlice> {
  if (max < 1) return slices;
  // ⚠️ DEUX RAISONS DE REPLIER, ET LA SECONDE COMPTE AUTANT QUE LA PREMIÈRE : le NOMBRE (au-delà de
  //    six, il faudrait cycler les teintes) et la PART (sous `DONUT_MIN_SHARE_BP`, l'arc est plus
  //    mince que le vide qui le sépare de son voisin). Sans la seconde, huit vendeurs à 1 % passaient
  //    le filtre du nombre et remplissaient la légende de lignes qui ne correspondaient à rien de
  //    visible sur l'anneau.
  const total = slices.reduce((t, x) => t + Math.max(0, x.value), 0);
  const plancher = total > 0 ? (total * DONUT_MIN_SHARE_BP) / 10_000 : 0;
  const tete = slices.filter((x, i) => i < max && Math.max(0, x.value) >= plancher);
  const queue = slices.filter((x) => !tete.includes(x));
  // ⚠️ ON NE REPLIE PAS UNE SEULE PART : « Autres (1) » n'apprend rien de plus que son nom, et coûte
  //    une teinte pour rien. Même règle qu'avant, elle vaut aussi pour le repli par part.
  if (queue.length <= 1) return slices;
  const reste = queue.reduce((t, x) => t + Math.max(0, x.value), 0);
  return [...tete, { key: '__autres__', label: `${label} (${queue.length})`, value: reste }];
}

export interface DonutProps {
  slices: DonutSlice[];
  size?: number;
  /** Rendu HUMAIN d'une part — l'infobulle s'en sert. Absent : la valeur brute. */
  format?: (v: number) => string;
  /** Ce qui s'inscrit au centre — un chiffre et sa légende, jamais un troisième niveau de donnée. */
  children?: ReactNode;
}

/**
 * Anneau de répartition — la part de chaque moyen de règlement, de chaque vendeur.
 *
 * ⚠️ UN VIDE SÉPARE LES SEGMENTS, et ce n'est pas décoratif : c'est le SECOND ENCODAGE qui rend deux
 * parts voisines distinguables quand la couleur seule ne suffit pas (daltonisme, impression, contraste
 * forcé). Sans lui, la palette ne serait pas conforme.
 * ⚠️ CE COMPOSANT NE PORTE PAS SA LÉGENDE : elle vit à côté, en lignes lisibles (pastille + nom +
 * valeur). Une part sans son étiquette obligerait à comparer des angles, ce que personne ne sait faire.
 */
/**
 * ANNEAU DE RÉPARTITION — des parts comparées ENTRE ELLES.
 *
 * ⚠️ CE N'EST PAS LA JAUGE, et il ne partage pas sa géométrie : la jauge vit dans un carré de 36 avec
 * un anneau de 5, l'anneau de répartition dans un repère de 140 avec un anneau de 20. Les avoir
 * confondus donnait un filet de jauge là où la maquette dessine un large disque.
 *
 * ⚠️ LE SURVOL AGRANDIT LA PART VISÉE ET ESTOMPE LES AUTRES. Sur un anneau, un simple changement de
 * teinte se confond avec la part voisine ; le relief, lui, ne se confond avec rien. C'est aussi ce qui
 * permet de suivre une part fine, qu'un pointeur recouvre entièrement.
 *
 * ⚠️ LE VIDE ENTRE LES PARTS EST LE SECOND ENCODAGE qui les rend distinguables en daltonisme, à
 * l'impression et en contraste forcé — la palette de séries n'est conforme qu'avec lui.
 */
export function Donut({ slices, size = DONUT.size, format, children }: DonutProps) {
  const [survol, setSurvol] = useState<number | null>(null);
  const [pointeur, setPointeur] = useState<{ x: number; y: number } | null>(null);
  const total = slices.reduce((s, x) => s + Math.max(0, x.value), 0);
  const segments = ringSegments(slices.map((x) => x.value), DONUT.gap, DONUT_CIRCUMFERENCE);

  const viser = (i: number, e: { clientX: number; clientY: number }) => {
    setSurvol(i);
    setPointeur({ x: e.clientX, y: e.clientY });
  };
  const lacher = () => { setSurvol(null); setPointeur(null); };
  const part = (v: number) => (total > 0 ? Math.round((Math.max(0, v) / total) * 100) : 0);

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      {/* ⚠️ La rotation est portée par le SVG ENTIER, comme la maquette : appliquée par arc, elle
          entrerait en conflit avec l'agrandissement au survol, qui a son propre `transform`. */}
      <svg
        width={size} height={size} viewBox={`0 0 ${DONUT.box} ${DONUT.box}`}
        style={{ transform: 'rotate(-90deg)', display: 'block' }}
        role="list" aria-label={t('Répartition')}
      >
        {segments.map((seg, i) => {
          const actif = survol === i;
          const estompe = survol !== null && !actif;
          const tranche = slices[i]!;
          return (
            <circle
              key={tranche.key}
              role="listitem"
              aria-label={`${tranche.label} : ${part(tranche.value)} %`}
              cx={DONUT.box / 2} cy={DONUT.box / 2} r={DONUT.radius}
              fill="none" stroke={donutVar(i)} strokeWidth={DONUT.strokeWidth} strokeLinecap="butt"
              strokeDasharray={`${seg.dash} ${DONUT_CIRCUMFERENCE - seg.dash}`}
              strokeDashoffset={seg.offset}
              onMouseEnter={(e) => viser(i, e)}
              onMouseMove={(e) => viser(i, e)}
              onMouseLeave={lacher}
              style={{
                transformBox: 'fill-box', transformOrigin: 'center',
                transform: actif ? `scale(${DONUT.hoverScale})` : 'none',
                opacity: estompe ? DONUT.dimOpacity : 1,
                transition: `transform ${DONUT.transitionMs}ms ease-out, opacity ${DONUT.transitionMs}ms ease-out`,
                cursor: 'pointer',
              }}
            />
          );
        })}
      </svg>
      {children ? (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
        }}>
          {children}
        </div>
      ) : null}
      {survol !== null && pointeur && slices[survol] ? (
        <ChartTip
          pointeur={pointeur}
          etiquette={slices[survol].label}
          valeur={format ? format(slices[survol].value) : String(slices[survol].value)}
          sous={t('{v} % du total', { v: part(slices[survol].value) })}
        />
      ) : null}
    </div>
  );
}

export interface GaugeProps {
  /** Part remplie, en points de base (2 500 = 25 %) — l'unité des ratios de la plateforme. */
  valueBp: number;
  size?: number;
  /**
   * LES PALIERS de l'indicateur — c'est EUX qui donnent à l'anneau le droit d'avoir une couleur.
   * ⚠️ Absents = aucune bande posée, donc anneau de MARQUE : un pourcentage sans paliers est une
   * proportion, pas une note (voir `gaugeTone`). Les valeurs sont des règles MÉTIER et vivent dans
   * `@orgazone/core-analytics` — jamais écrites dans un écran.
   */
  bands?: GaugeThresholds | null;
  /**
   * TON IMPOSÉ, quand il ne se déduit pas de la valeur — en pratique `unmeasured`, la seule chose
   * qu'une jauge sait d'elle-même sans mesure.
   * ⚠️ Il remplace l'ancien `stroke`, qui prenait une COULEUR libre : l'appelant nomme un rôle, la
   * charte choisit la teinte. Même doctrine que les pastilles et les tuiles de repère.
   */
  tone?: GaugeTone;
  /**
   * Nom accessible. Défaut : le pourcentage.
   * ⚠️ À FOURNIR DÈS QUE LA VALEUR N'EST PAS MESURÉE : sur une jauge vide, le défaut annonce « 0 % » à
   * un lecteur d'écran — c'est-à-dire une mesure, là où il n'y en a aucune (§23.0).
   */
  label?: string;
  children?: ReactNode;
}

/**
 * Jauge — UNE valeur rapportée à son maximum (taux de transformation, part de clients identifiés).
 *
 * ⚠️ CE N'EST PAS UN DONUT À UNE PART : une jauge compare une valeur à 100 %, un donut compare des
 * parts entre elles. Les confondre ferait lire « 30 % du total » là où on montre « 30 % de l'objectif ».
 * ⚠️ Géométrie et bout arrondi repris de la maquette au chiffre près.
 */
export function Gauge({ valueBp, size = 148, bands, tone, label, children }: GaugeProps) {
  const ton = tone ?? gaugeTone(valueBp, bands);
  const trait = gaugeDash(valueBp);
  const part = trait / RING_CIRCUMFERENCE;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${BOITE} ${BOITE}`} role="img" aria-label={label ?? `${Math.round(part * 100)} %`}>
        <circle cx={BOITE / 2} cy={BOITE / 2} r={CHART.ringRadius} fill="none" stroke="var(--ink-100)" strokeWidth={CHART.ringWidth} />
        {part > 0 ? (
          <circle
            cx={BOITE / 2} cy={BOITE / 2} r={CHART.ringRadius}
            fill="none" stroke={`var(--${GAUGE_TONES[ton]})`} strokeWidth={CHART.ringWidth} strokeLinecap="round"
            strokeDasharray={`${trait} ${RING_CIRCUMFERENCE - trait}`}
            transform={`rotate(-90 ${BOITE / 2} ${BOITE / 2})`}
          />
        ) : null}
      </svg>
      {children ? (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
        }}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

// ── LÉGENDE ───────────────────────────────────────────────────────────────────────────────────────

export interface LegendRow {
  key: string;
  label: string;
  value: string;
}

/**
 * La légende d'un anneau — pastille, nom, valeur alignée à droite.
 *
 * ⚠️ ELLE EST OBLIGATOIRE dès deux séries, et ce n'est pas une politesse : la palette n'est conforme
 * que si l'identité est lisible autrement que par la couleur. C'est aussi elle qui lève l'avertissement
 * de contraste des teintes claires sur papier blanc.
 * ⚠️ Le NOM et la VALEUR portent l'encre du texte, jamais la couleur de la série (cf. l'en-tête).
 */
export function ChartLegend({ rows, style }: { rows: LegendRow[]; style?: CSSProperties }) {
  return (
    <div style={{ padding: '6px 12px 12px', ...style }}>
      {rows.map((r, i) => (
        <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: 8, borderRadius: 'var(--r-ctrl)' }}>
          <span style={{ width: 9, height: 9, borderRadius: 'var(--r-r3)', background: seriesVar(i), flexShrink: 0 }} />
          <span style={{
            flex: 1, fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)',
            minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {r.label}
          </span>
          <span style={{
            width: 100, textAlign: 'right', fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)',
            fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
          }}>
            {r.value}
          </span>
        </div>
      ))}
    </div>
  );
}
